import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgxSpinnerService } from "ngx-spinner";
import { forkJoin } from "rxjs";
import Swal from "sweetalert2";
import { HttpHelpService } from "../../service/http-help.service";

type Lang = "ar" | "en" | "he";
type ActivationMode = "admin_grant" | "payment_link";

@Component({
	selector: "app-admin-merchant-subscriptions",
	templateUrl: "./admin-merchant-subscriptions.component.html",
	styleUrls: ["./admin-merchant-subscriptions.component.scss"],
})
export class AdminMerchantSubscriptionsComponent implements OnInit {
	curentLang: Lang = (localStorage.getItem("curentLang") as Lang) || "ar";
	term: string = "";

	filterStatus: "" | "active" | "cancelled" | "expired" = "";
	filterPlanId: number | null = null;
	filterAutoRenew: "" | "true" | "false" = "";
	filterDateFrom: string = "";
	filterDateTo: string = "";

	/** When true, CSV export uses the same rows as the table (`filteredSubscriptions`). When false, exports all loaded rows. */
	exportFilteredOnly: boolean = true;

	merchantOptions: any[] = [];
	planOptions: any[] = [];
	docExtensionPlans: any[] = [];
	companyExtensionPlans: any[] = [];
	tokenExtensionPlans: any[] = [];
	subscriptions: any[] = [];
	activeExtensionTab: "grant" | "manual" = "grant";

	selectedMerchantId: number | null = null;
	selectedMerchantStatus: any = null;
	selectedMerchantHistory: any = null;
	isDetailsLoading = false;
	lastPaymentUrl: string | null = null;
	lastOrderId: string | null = null;

	createForm: FormGroup = new FormGroup({
		merchant_id: new FormControl(null, [Validators.required, Validators.min(1)]),
		plan_id: new FormControl(null, [Validators.required, Validators.min(1)]),
		start_date: new FormControl("", [Validators.required]),
		activation_mode: new FormControl<ActivationMode>("admin_grant", [Validators.required]),
		language: new FormControl<Lang>("ar"),
	});

	extensionForm: FormGroup = new FormGroup({
		merchant_id: new FormControl(null, [Validators.required, Validators.min(1)]),
		extension_type: new FormControl<"doc" | "company" | "token">("doc", [Validators.required]),
		plan_id: new FormControl(null, [Validators.required, Validators.min(1)]),
		activation_mode: new FormControl<ActivationMode>("admin_grant", [Validators.required]),
		language: new FormControl<Lang>("ar"),
	});

	manualAdjustForm: FormGroup = new FormGroup({
		merchant_id: new FormControl(null, [Validators.required, Validators.min(1)]),
		extra_documents: new FormControl<number | null>(null, [Validators.min(0)]),
		extra_companies: new FormControl<number | null>(null, [Validators.min(0)]),
		extra_days: new FormControl<number | null>(null, [Validators.min(0)]),
		/** Sent on adjust API as `extra_tokens` (expense AI analysis tokens). */
		extra_tokens: new FormControl<number | null>(null, [Validators.min(0)]),
	});

	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		private router: Router,
		private modalService: NgbModal
	) {}

	ngOnInit(): void {
		this.refreshAll();
	}

	t(ar: string, en: string, he: string): string {
		if (this.curentLang === "ar") return ar;
		if (this.curentLang === "he") return he;
		return en;
	}

	/**
	 * One row per merchant — latest effective subscription:
	 * prefer an active row when present, otherwise the most recently updated record.
	 */
	get latestSubscriptionsByMerchant(): any[] {
		const byMerchant = new Map<number, any[]>();
		for (const item of this.subscriptions) {
			const merchantId = Number(item?.merchant_id);
			if (!Number.isFinite(merchantId)) continue;
			const list = byMerchant.get(merchantId) || [];
			list.push(item);
			byMerchant.set(merchantId, list);
		}
		return Array.from(byMerchant.values())
			.map((items) => this.pickRepresentativeSubscription(items))
			.filter((item): item is any => !!item)
			.sort((a, b) => this.getSubscriptionSortTime(b) - this.getSubscriptionSortTime(a));
	}

	private pickRepresentativeSubscription(candidates: any[]): any | null {
		if (!candidates.length) return null;
		const active = candidates.filter((item) => this.getEffectiveSubscriptionStatus(item) === "active");
		const pool = active.length ? active : candidates;
		return pool.reduce((best, item) =>
			!best || this.getSubscriptionSortTime(item) > this.getSubscriptionSortTime(best) ? item : best
		);
	}

	get filteredSubscriptions(): any[] {
		const keyword = (this.term || "").toLowerCase().trim();
		const dateFrom = this.filterDateFrom ? new Date(this.filterDateFrom).getTime() : null;
		const dateTo = this.filterDateTo ? new Date(this.filterDateTo + "T23:59:59").getTime() : null;

		return this.latestSubscriptionsByMerchant.filter((item) => {
			if (
				this.filterStatus &&
				this.getEffectiveSubscriptionStatus(item) !== this.filterStatus
			) {
				return false;
			}
			if (this.filterPlanId !== null && Number(item.plan?.id) !== Number(this.filterPlanId)) return false;
			if (this.filterAutoRenew !== "") {
				const expected = this.filterAutoRenew === "true";
				if (item.auto_renew !== expected) return false;
			}
			if (dateFrom !== null) {
				const start = new Date(item.start_date).getTime();
				if (start < dateFrom) return false;
			}
			if (dateTo !== null) {
				const start = new Date(item.start_date).getTime();
				if (start > dateTo) return false;
				/* Hide rows whose subscription ends after «Start until» so the table matches the chosen window. */
				if (item.end_date) {
					const end = new Date(item.end_date).getTime();
					if (Number.isFinite(end) && end > dateTo) return false;
				}
			}
			if (keyword) {
				const merchantName = this.getMerchantName(item.merchant_id).toLowerCase();
				const planName = this.getPlanDisplayName(item.plan).toLowerCase();
				const matches =
					merchantName.includes(keyword) ||
					planName.includes(keyword) ||
					String(item.merchant_id).includes(keyword) ||
					String(item.plan?.id || item.plan_id || "").includes(keyword) ||
					String(this.getEffectiveSubscriptionStatus(item) || "").includes(keyword) ||
					String(item.status || "").toLowerCase().includes(keyword) ||
					String(item.subscription_id || "").includes(keyword);
				if (!matches) return false;
			}
			return true;
		});
	}

	get uniquePlanOptions(): any[] {
		const seen = new Set<number>();
		const result: any[] = [];
		for (const sub of this.latestSubscriptionsByMerchant) {
			const plan = sub.plan;
			if (plan && !seen.has(Number(plan.id))) {
				seen.add(Number(plan.id));
				result.push(plan);
			}
		}
		return result;
	}

	resetFilters(): void {
		this.term = "";
		this.filterStatus = "";
		this.filterPlanId = null;
		this.filterAutoRenew = "";
		this.filterDateFrom = "";
		this.filterDateTo = "";
	}

	/**
	 * Normalizes subscription rows for filtering and badges:
	 * API may use different casing / "canceled" vs "cancelled"; some rows stay `active`
	 * in `status` even after `end_date` passed.
	 */
	private getSubscriptionSortTime(item: any): number {
		const dateCandidates = [item?.updated_at, item?.created_at, item?.start_date];
		for (const value of dateCandidates) {
			if (value === null || value === undefined || value === "") continue;
			const ms = new Date(value).getTime();
			if (Number.isFinite(ms)) return ms;
		}
		const id = Number(item?.subscription_id);
		return Number.isFinite(id) ? id : 0;
	}

	getMerchantById(merchantId: number): any | null {
		return (
			(this.merchantOptions || []).find(
				(item: any) => Number(item.merchant_id) === Number(merchantId)
			) || null
		);
	}

	formatDocumentsUsage(item: any): string {
		const used = item?.usage?.documents_used ?? 0;
		const limit = item?.usage?.documents_limit ?? 0;
		return `${used} / ${limit}`;
	}

	formatCompaniesUsage(item: any): string {
		const used = item?.usage?.companies_used ?? 0;
		const limit = item?.usage?.companies_limit ?? 0;
		return `${used} / ${limit}`;
	}

	formatHistoryDocumentsUsage(item: any): string {
		const used = item?.usage?.documents_used ?? item?.documents_used ?? "-";
		const limit = item?.usage?.documents_limit ?? item?.documents_limit ?? "-";
		return `${used} / ${limit}`;
	}

	formatHistoryCompaniesUsage(item: any): string {
		const used = item?.usage?.companies_used ?? item?.companies_used ?? "-";
		const limit = item?.usage?.companies_limit ?? item?.companies_limit ?? "-";
		return `${used} / ${limit}`;
	}

	getHistoryEffectiveStatusLabel(item: any): string {
		return this.subscriptionEffectiveStatusLabel(item);
	}

	getEffectiveSubscriptionStatus(item: any): "active" | "cancelled" | "expired" {
		const raw = String(item?.status ?? "")
			.trim()
			.toLowerCase();

		if (raw === "cancelled" || raw === "canceled") {
			return "cancelled";
		}

		const endMs = item?.end_date ? new Date(item.end_date).getTime() : NaN;
		const periodEnded =
			Number.isFinite(endMs) && endMs < Date.now();

		if (raw === "expired" || periodEnded) {
			return "expired";
		}

		return "active";
	}

	/** Label for badges / chips (localized). */
	subscriptionEffectiveStatusLabel(item: any): string {
		switch (this.getEffectiveSubscriptionStatus(item)) {
			case "cancelled":
				return this.t("ملغي", "Cancelled", "מבוטל");
			case "expired":
				return this.t("منتهي", "Expired", "פג תוקף");
			default:
				return this.t("نشط", "Active", "פעיל");
		}
	}

	/** Aggregates on latest subscription per merchant (ignores toolbar search / non-status filters). */
	get subscriptionStatusSummary(): { active: number; expired: number; cancelled: number } {
		let active = 0;
		let expired = 0;
		let cancelled = 0;
		for (const item of this.latestSubscriptionsByMerchant) {
			const s = this.getEffectiveSubscriptionStatus(item);
			if (s === "cancelled") cancelled++;
			else if (s === "expired") expired++;
			else active++;
		}
		return { active, expired, cancelled };
	}

	setStatusFilter(status: "" | "active" | "cancelled" | "expired"): void {
		this.filterStatus = status;
	}

	exportToCsv(): void {
		const rows = this.exportFilteredOnly ? this.filteredSubscriptions : this.latestSubscriptionsByMerchant;
		if (!rows.length) {
			this.showWarning(
				this.exportFilteredOnly
					? this.t(
							"لا توجد نتائج مطابقة للمرشحات للتصدير.",
							"No rows match the current filters to export.",
							"אין שורות התואמות למסננים לייצוא."
						)
					: this.t("لا توجد بيانات للتصدير", "No data to export", "אין נתונים לייצוא")
			);
			return;
		}

		const headers = [
			this.t("رقم الاشتراك", "Subscription ID", "מספר מנוי"),
			this.t("رقم التاجر", "Merchant ID", "מספר סוחר"),
			this.t("اسم التاجر", "Merchant Name", "שם סוחר"),
			this.t("الخطة", "Plan", "תוכנית"),
			this.t("سعر الخطة", "Plan Price", "מחיר תוכנית"),
			this.t("الحالة", "Status", "סטטוס"),
			this.t("تجديد تلقائي", "Auto Renew", "חידוש אוטומטי"),
			this.t("تاريخ البداية", "Start Date", "תאריך התחלה"),
			this.t("تاريخ الانتهاء", "End Date", "תאריך סיום"),
			this.t("المستندات المستخدمة", "Documents Used", "מסמכים בשימוש"),
			this.t("حد المستندات", "Documents Limit", "מגבלת מסמכים"),
			this.t("المستندات المتبقية", "Documents Remaining", "מסמכים נותרים"),
			this.t("الشركات المستخدمة", "Companies Used", "חברות בשימוש"),
			this.t("حد الشركات", "Companies Limit", "מגבלת חברות"),
			this.t("الشركات المتبقية", "Companies Remaining", "חברות נותרות"),
		];

		const escape = (val: any): string => {
			const str = val === null || val === undefined ? "" : String(val);
			if (str.includes(",") || str.includes('"') || str.includes("\n")) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		};

		const formatDate = (iso: string): string => {
			if (!iso) return "";
			try {
				const d = new Date(iso);
				return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
			} catch {
				return iso;
			}
		};

		const csvRows = rows.map((item) => [
			escape(item.subscription_id),
			escape(item.merchant_id),
			escape(this.getMerchantName(item.merchant_id)),
			escape(this.getPlanDisplayName(item.plan)),
			escape(item.plan?.price ?? ""),
			escape(this.getEffectiveSubscriptionStatus(item)),
			escape(item.auto_renew ? this.t("نعم", "Yes", "כן") : this.t("لا", "No", "לא")),
			escape(formatDate(item.start_date)),
			escape(formatDate(item.end_date)),
			escape(item.usage?.documents_used ?? 0),
			escape(item.usage?.documents_limit ?? 0),
			escape(item.usage?.documents_remaining ?? 0),
			escape(item.usage?.companies_used ?? 0),
			escape(item.usage?.companies_limit ?? 0),
			escape(item.usage?.companies_remaining ?? 0),
		].join(","));

		const bom = "\uFEFF";
		const csvContent = bom + [headers.join(","), ...csvRows].join("\r\n");
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		const scope = this.exportFilteredOnly ? "filtered" : "all";
		a.download = `merchant-subscriptions-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}

	refreshAll(): void {
		this.spinner.show();
		this.service.getAllMerchantInfo().subscribe({
			next: (merchants) => {
				this.merchantOptions = (merchants || []).map((m: any) => ({
					...m,
					display_label: this.getMerchantDisplayName(m),
				}));
				forkJoin({
					mainPlans: this.service.getAdminV2MainPlans(),
					docPlans: this.service.getAdminV2DocExtensionPlans(),
					companyPlans: this.service.getAdminV2CompanyExtensionPlans(),
					tokenPlans: this.service.getAdminV2TokenExtensionPlans(),
				}).subscribe({
					next: ({ mainPlans, docPlans, companyPlans, tokenPlans }) => {
						this.planOptions = (mainPlans || []).filter((p: any) => p.is_active);
						this.docExtensionPlans = (docPlans || []).filter((p: any) => p.is_active);
						this.companyExtensionPlans = (companyPlans || []).filter((p: any) => p.is_active);
						this.tokenExtensionPlans = (tokenPlans || []).filter((p: any) => p.is_active);
						this.loadAllSubscriptions();
					},
					error: (error) => {
						this.spinner.hide();
						this.showError(this.extractErrorMessage(error));
					},
				});
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	loadAllSubscriptions(): void {
		this.service.getAdminMerchantSubscriptions().subscribe({
			next: (res: any) => {
				let list = res;
				if (!Array.isArray(list) && Array.isArray(res?.data)) {
					list = res.data;
				} else if (!Array.isArray(list) && res?.data?.items) {
					list = res.data.items;
				}
				this.subscriptions = Array.isArray(list) ? list : [];
				this.spinner.hide();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	createSubscription(): void {
		if (this.createForm.invalid) {
			this.createForm.markAllAsTouched();
			this.showWarning(
				this.t(
					"يرجى تعبئة الحقول المطلوبة بشكل صحيح",
					"Please fill all required fields correctly",
					"אנא מלא את כל השדות הנדרשים בצורה תקינה"
				)
			);
			return;
		}

		const value = this.createForm.value;
		const payload = {
			merchant_id: Number(value.merchant_id),
			plan_id: Number(value.plan_id),
			start_date: new Date(value.start_date).toISOString(),
			activation_mode: value.activation_mode as ActivationMode,
			language: value.language as Lang,
		};

		this.spinner.show();
		this.service.createAdminMerchantSubscription(payload).subscribe({
			next: (res) => {
				this.spinner.hide();

				if (res?.type === "payment_link" && res?.payment_url) {
					this.lastPaymentUrl = res.payment_url;
					this.lastOrderId = res.order_id || null;
					Swal.fire({
						icon: "success",
						title: this.resolveMessage(res),
						html: `<div style="word-break:break-all">${res.payment_url}</div>`,
						showDenyButton: true,
						showCancelButton: true,
						confirmButtonText: this.t("فتح الرابط", "Open Link", "פתח קישור"),
						denyButtonText: this.t("نسخ الرابط", "Copy Link", "העתק קישור"),
						cancelButtonText: this.t("إغلاق", "Close", "סגור"),
					}).then((result) => {
						if (result.isConfirmed) {
							window.open(res.payment_url, "_blank");
						}
						if (result.isDenied) {
							this.copyPaymentLink(res.payment_url);
						}
					});
				} else {
					this.showSuccess(this.resolveMessage(res));
				}

				this.createForm.patchValue({ activation_mode: "admin_grant", language: this.curentLang });
				this.createForm.get("merchant_id")?.reset();
				this.createForm.get("plan_id")?.reset();
				this.createForm.get("start_date")?.reset();
				this.modalService.dismissAll();
				this.loadAllSubscriptions();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	openCreateSubscriptionModal(content: any): void {
		this.createForm.patchValue({ activation_mode: "admin_grant", language: this.curentLang });
		this.modalService.open(content, { size: "xl", centered: true });
	}

	openExtensionModal(content: any): void {
		this.activeExtensionTab = "grant";
		this.extensionForm.reset({
			merchant_id: null,
			extension_type: "doc",
			plan_id: null,
			activation_mode: "admin_grant",
			language: this.curentLang,
		});
		this.manualAdjustForm.reset({
			merchant_id: null,
			extra_documents: null,
			extra_companies: null,
			extra_days: null,
			extra_tokens: null,
		});
		this.modalService.open(content, { size: "xl", centered: true });
	}

	openMerchantDetails(content: any, merchantId: number): void {
		this.modalService.open(content, { size: "xl", centered: true });
		this.viewMerchantDetails(merchantId);
	}

	viewMerchantDetails(merchantId: number): void {
		this.selectedMerchantId = merchantId;
		this.selectedMerchantStatus = null;
		this.selectedMerchantHistory = null;
		this.isDetailsLoading = true;
		this.service.getAdminMerchantSubscriptionStatus(merchantId).subscribe({
			next: (statusRes) => {
				this.selectedMerchantStatus = statusRes || null;
				this.service.getAdminMerchantSubscriptionHistory(merchantId).subscribe({
					next: (historyRes) => {
						this.selectedMerchantHistory = historyRes || null;
						this.isDetailsLoading = false;
					},
					error: (error) => {
						this.isDetailsLoading = false;
						this.showError(this.extractErrorMessage(error));
					},
				});
			},
			error: (error) => {
				this.isDetailsLoading = false;
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	disableActiveSubscription(merchantId: number): void {
		Swal.fire({
			icon: "warning",
			title: this.t(
				"هل تريد تعطيل الاشتراك النشط لهذا التاجر؟",
				"Do you want to disable the active subscription for this merchant?",
				"האם להשבית את המנוי הפעיל של סוחר זה?"
			),
			showCancelButton: true,
			confirmButtonText: this.t("تعطيل", "Disable", "השבת"),
			cancelButtonText: this.t("إلغاء", "Cancel", "ביטול"),
		}).then((result) => {
			if (!result.isConfirmed) return;

			this.spinner.show();
			this.service.disableAdminMerchantSubscription(merchantId).subscribe({
				next: (res) => {
					this.spinner.hide();
					this.showSuccess(this.resolveMessage(res));
					this.loadAllSubscriptions();
					if (this.selectedMerchantId === merchantId) {
						this.viewMerchantDetails(merchantId);
					}
				},
				error: (error) => {
					this.spinner.hide();
					this.showError(this.extractErrorMessage(error));
				},
			});
		});
	}

	getMerchantName(merchantId: number): string {
		const merchant = (this.merchantOptions || []).find(
			(item: any) => Number(item.merchant_id) === Number(merchantId)
		);
		if (!merchant) return `#${merchantId}`;
		return this.getMerchantDisplayName(merchant);
	}

	getMerchantDisplayName(merchant: any): string {
		if (!merchant) return "-";
		if (this.curentLang === "ar") {
			return merchant.merchant_name_ar || merchant.merchant_name_en || merchant.merchant_name_he || `#${merchant.merchant_id}`;
		}
		if (this.curentLang === "he") {
			return merchant.merchant_name_he || merchant.merchant_name_en || merchant.merchant_name_ar || `#${merchant.merchant_id}`;
		}
		return merchant.merchant_name_en || merchant.merchant_name_ar || merchant.merchant_name_he || `#${merchant.merchant_id}`;
	}

	getPlanName(planId: number): string {
		const plan = (this.planOptions || []).find((item: any) => Number(item.id) === Number(planId));
		if (!plan) return `#${planId}`;
		if (this.curentLang === "ar") return plan.name_ar || plan.name_en;
		if (this.curentLang === "he") return plan.name_he || plan.name_en;
		return plan.name_en;
	}

	getPlanDisplayName(plan: any): string {
		if (!plan) return "-";
		if (this.curentLang === "ar") return plan.name_ar || plan.name_en || plan.name_he || `#${plan.id}`;
		if (this.curentLang === "he") return plan.name_he || plan.name_en || plan.name_ar || `#${plan.id}`;
		return plan.name_en || plan.name_ar || plan.name_he || `#${plan.id}`;
	}

	getPlanOptionLabel(plan: any): string {
		if (!plan) return "-";
		const parts: string[] = [];
		if (plan.documents_limit !== undefined && plan.documents_limit !== null) {
			parts.push(`${this.t("المستندات", "Docs", "מסמכים")}: ${plan.documents_limit}`);
		}
		if (plan.companies_limit !== undefined && plan.companies_limit !== null) {
			parts.push(`${this.t("الشركات", "Companies", "חברות")}: ${plan.companies_limit}`);
		}
		if (plan.extra_documents !== undefined && plan.extra_documents !== null) {
			parts.push(`${this.t("مستندات إضافية", "Extra Docs", "מסמכים נוספים")}: ${plan.extra_documents}`);
		}
		if (plan.extra_companies !== undefined && plan.extra_companies !== null) {
			parts.push(`${this.t("شركات إضافية", "Extra Companies", "חברות נוספות")}: ${plan.extra_companies}`);
		}
		if (plan.extra_tokens !== undefined && plan.extra_tokens !== null) {
			parts.push(`${this.t("توكنات إضافية", "Extra Tokens", "טוקנים נוספים")}: ${plan.extra_tokens}`);
		}
		const details = parts.length ? ` - ${parts.join(" | ")}` : "";
		return `${this.getPlanDisplayName(plan)}${details}`;
	}

	get extensionPlanOptions(): any[] {
		const type = this.extensionForm.get("extension_type")?.value;
		if (type === "company") return this.companyExtensionPlans;
		if (type === "token") return this.tokenExtensionPlans;
		return this.docExtensionPlans;
	}

	onExtensionTypeChange(): void {
		this.extensionForm.get("plan_id")?.reset(null);
	}

	merchantSearchFn = (term: string, item: any): boolean => {
		const source = item?.value ?? item ?? {};
		const haystack = [
			source.merchant_name_ar,
			source.merchant_name_en,
			source.merchant_name_he,
			source.id_number,
			source.merchant_id,
		]
			.filter((v) => v !== null && v !== undefined)
			.join(" ")
			.toLowerCase();
		return haystack.includes((term || "").toLowerCase());
	};

	isInvalid(controlName: string): boolean {
		const control = this.createForm.get(controlName);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	isInvalidInForm(form: FormGroup, controlName: string): boolean {
		const control = form.get(controlName);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	backToPlans(): void {
		this.router.navigate(["/dashboard/plans-info"]);
	}

	copyPaymentLink(url?: string): void {
		const link = url || this.lastPaymentUrl;
		if (!link) return;

		if (navigator?.clipboard?.writeText) {
			navigator.clipboard.writeText(link).then(() => {
				this.showSuccess(this.t("تم نسخ رابط الدفع", "Payment link copied", "קישור התשלום הועתק"));
			}).catch(() => this.copyPaymentLinkFallback(link));
			return;
		}

		this.copyPaymentLinkFallback(link);
	}

	createMerchantExtension(): void {
		if (this.extensionForm.invalid) {
			this.extensionForm.markAllAsTouched();
			this.showWarning(
				this.t(
					"يرجى تعبئة بيانات التمديد بشكل صحيح",
					"Please fill extension fields correctly",
					"אנא מלא את שדות ההארכה בצורה תקינה"
				)
			);
			return;
		}

		const value = this.extensionForm.value;
		const payload = {
			merchant_id: Number(value.merchant_id),
			plan_id: Number(value.plan_id),
			activation_mode: value.activation_mode as ActivationMode,
			language: value.language as Lang,
		};

		const request$ =
			value.extension_type === "company"
				? this.service.createAdminMerchantCompanyExtension(payload)
				: value.extension_type === "token"
					? this.service.createAdminMerchantTokenExtension(payload)
					: this.service.createAdminMerchantDocExtension(payload);

		this.spinner.show();
		request$.subscribe({
			next: (res) => {
				this.spinner.hide();
				if (res?.type === "payment_link" && res?.payment_url) {
					this.lastPaymentUrl = res.payment_url;
					this.lastOrderId = res.order_id || null;
					Swal.fire({
						icon: "success",
						title: this.resolveMessage(res),
						html: `<div style="word-break:break-all">${res.payment_url}</div>`,
						showDenyButton: true,
						showCancelButton: true,
						confirmButtonText: this.t("فتح الرابط", "Open Link", "פתח קישור"),
						denyButtonText: this.t("نسخ الرابط", "Copy Link", "העתק קישור"),
						cancelButtonText: this.t("إغلاق", "Close", "סגור"),
					}).then((result) => {
						if (result.isConfirmed) window.open(res.payment_url, "_blank");
						if (result.isDenied) this.copyPaymentLink(res.payment_url);
					});
				} else {
					this.showSuccess(this.resolveMessage(res));
				}

				this.extensionForm.patchValue({
					activation_mode: "admin_grant",
					language: this.curentLang,
				});
				this.extensionForm.get("merchant_id")?.reset();
				this.extensionForm.get("plan_id")?.reset();
				this.modalService.dismissAll();
				this.loadAllSubscriptions();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	applyManualAdjust(): void {
		if (this.manualAdjustForm.invalid) {
			this.manualAdjustForm.markAllAsTouched();
			this.showWarning(
				this.t(
					"يرجى إدخال بيانات صحيحة",
					"Please provide valid values",
					"אנא הזן ערכים תקינים"
				)
			);
			return;
		}

		const merchantId = Number(this.manualAdjustForm.value.merchant_id);
		const extraDocuments = Number(this.manualAdjustForm.value.extra_documents || 0);
		const extraCompanies = Number(this.manualAdjustForm.value.extra_companies || 0);
		const extraDays = Number(this.manualAdjustForm.value.extra_days || 0);
		const extraTokens = Number(this.manualAdjustForm.value.extra_tokens || 0);

		if (!merchantId) {
			this.showWarning(this.t("اختيار التاجر مطلوب", "Merchant is required", "בחירת סוחר היא חובה"));
			return;
		}

		if (extraDocuments <= 0 && extraCompanies <= 0 && extraDays <= 0 && extraTokens <= 0) {
			this.showWarning(
				this.t(
					"أدخل قيمة واحدة على الأقل أكبر من صفر",
					"Enter at least one value greater than zero",
					"הזן לפחות ערך אחד גדול מאפס"
				)
			);
			return;
		}

		const payload: any = {};
		if (extraDocuments > 0) payload.extra_documents = extraDocuments;
		if (extraCompanies > 0) payload.extra_companies = extraCompanies;
		if (extraDays > 0) payload.extra_days = extraDays;
		if (extraTokens > 0) payload.extra_tokens = extraTokens;

		this.spinner.show();
		this.service.adjustAdminMerchantSubscription(merchantId, payload).subscribe({
			next: (res) => {
				this.spinner.hide();
				this.showSuccess(this.resolveMessage(res));
				this.manualAdjustForm.patchValue({
					extra_documents: null,
					extra_companies: null,
					extra_days: null,
					extra_tokens: null,
				});
				this.modalService.dismissAll();
				this.loadAllSubscriptions();
				if (this.selectedMerchantId === merchantId) {
					this.viewMerchantDetails(merchantId);
				}
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	private copyPaymentLinkFallback(link: string): void {
		const textarea = document.createElement("textarea");
		textarea.value = link;
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
		this.showSuccess(this.t("تم نسخ رابط الدفع", "Payment link copied", "קישור התשלום הועתק"));
	}

	private resolveMessage(res: any): string {
		if (this.curentLang === "ar" && res?.messageAr) return res.messageAr;
		if (this.curentLang === "he" && res?.messageHe) return res.messageHe;
		return res?.messageEn || res?.message || this.t("تمت العملية بنجاح", "Operation done successfully", "הפעולה בוצעה בהצלחה");
	}

	private extractErrorMessage(error: any): string {
		const apiError = error?.error;
		if (this.curentLang === "ar" && apiError?.messageAr) return apiError.messageAr;
		if (this.curentLang === "he" && apiError?.messageHe) return apiError.messageHe;
		if (this.curentLang === "en" && apiError?.messageEn) return apiError.messageEn;
		if (Array.isArray(apiError?.message)) return apiError.message.join(", ");
		if (typeof apiError?.message === "string") return apiError.message;
		return this.t("حدث خطأ غير متوقع", "Unexpected error occurred", "אירעה שגיאה לא צפויה");
	}

	private showSuccess(title: string): void {
		Swal.fire({ icon: "success", title });
	}

	private showError(title: string): void {
		Swal.fire({ icon: "error", title });
	}

	private showWarning(title: string): void {
		Swal.fire({ icon: "warning", title });
	}
}
